import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { SessionUser } from '../api';
import {
  ArchiveButton,
  ArchivedToggle,
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  Modal,
  ModalActions,
  PageHead,
  Pill,
  TableScroll,
  formatDate,
  useAction,
  useLoad,
} from '../ui';
import { PasswordField } from '../password';
import { ResetPasswordButton } from '../ResetPassword';

/**
 * Sorting Company's own logins.
 *
 * These belong to no organization, so they appear under no organization's users and
 * had no screen at all — the only way to create one was the staff:create script on
 * the server, which meant SSH access to onboard a colleague.
 */
export function StaffUsers({ user }: { user: SessionUser }) {
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);

  const staff = useLoad(() => api.users.listStaff(showArchived), [showArchived]);
  const action = useAction();

  return (
    <>
      <PageHead
        title="Staff"
        hint="Sorting Company logins. These can reach every customer, so keep the list short."
        actions={
          <>
            <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
            <Button onClick={() => setAdding(true)}>Add staff login</Button>
          </>
        }
      />

      <ErrorBanner message={staff.error ?? action.error} />

      <Modal open={adding} title="Add a staff login" onClose={() => setAdding(false)}>
        <NewStaffUser
          onCreated={() => {
            setAdding(false);
            staff.reload();
          }}
        />
      </Modal>

      <Card>
        {staff.loading && <Empty>Loading…</Empty>}
        {!staff.loading && staff.data?.length === 0 && (
          <Empty>
            {showArchived ? 'No staff logins, archived or otherwise.' : 'No staff logins.'}
          </Empty>
        )}

        {staff.data && staff.data.length > 0 && (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {staff.data.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="mr-2">{row.email}</span>
                      {row.id === user.id && <Pill>you</Pill>}
                    </td>
                    <td>
                      <Pill tone={row.status === 'active' ? 'on' : 'warn'}>{row.status}</Pill>
                    </td>
                    <td className="text-muted">{formatDate(row.lastLoginAt)}</td>
                    <td className="text-right">
                      {/* Neither is offered on your own login: archiving it would lock you
                          out mid-session, and your own password is changed from the account
                          menu. The server refuses both too. */}
                      {row.id !== user.id && (
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          {/* An archived login cannot sign in, so a reset would do nothing. */}
                          {row.status === 'active' && (
                            <ResetPasswordButton
                              userId={row.id}
                              email={row.email}
                              busy={action.busy}
                              run={action.run}
                            />
                          )}
                          <ArchiveButton
                            status={row.status}
                            label={row.email}
                            cascadeNote="They will not be able to sign in to any customer."
                            busy={action.busy}
                            onArchive={() =>
                              void action.run(() => api.users.archive(row.id)).then(staff.reload)
                            }
                            onRestore={() =>
                              void action.run(() => api.users.restore(row.id)).then(staff.reload)
                            }
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Card>
    </>
  );
}

function NewStaffUser({ onCreated }: { onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(() =>
      // No orgId: the server rejects a staff login that carries one.
      api.users.create({ email: email.trim(), password, role: 'staff' }),
    );
    if (ok) {
      setEmail('');
      setPassword('');
      onCreated();
    }
  }

  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />

      <p className="m-0 text-muted">
        A staff login can see and change every customer's configuration.
      </p>

      <Field label="Email">
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      <Field label="Password" hint="At least 8 characters. Generate one, copy it, and tell them out of band.">
        <PasswordField value={password} onChange={setPassword} />
      </Field>

      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Creating…' : 'Create staff login'}
        </Button>
      </ModalActions>
    </form>
  );
}
